import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import Papa from 'papaparse';

export default function ProjectManager() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    type: '',
    status: '',
    customerId: '',
    customerName: '',
    color: '',
    notes: '',
    contractNumber: '',
    language: 'Magyar / Hungarian',
    paymentDetails: {
      nftCount: 0,
      splitPayment: '',
      nftValueUSDT: 0,
      currency: 'USDT',
      paymentType: 'KRIPTO',
      paymentMethod: 'KRIPTO (Utalás)',
      amount: 0
    }
  });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateRange: 'all'
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsRef = collection(db, 'projects');
      const q = query(projectsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      setProjects(projectsData);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Hiba történt a projektek betöltésekor');
      toast.error('Nem sikerült betölteni a projekteket');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Befizetést várunk - KRIPTO': 'bg-yellow-100 text-yellow-800',
      'Díjbkérő kiküldése - HUN': 'bg-blue-100 text-blue-800',
      'Feldolgozva': 'bg-green-100 text-green-800',
      'Inaktív': 'bg-gray-100 text-gray-800',
      'Stake kiírás': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...newProject,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isArchived: false,
        isDeleted: false
      };

      await addDoc(collection(db, 'projects'), projectData);
      toast.success('Projekt sikeresen létrehozva');
      setIsModalOpen(false);
      setNewProject({
        name: '',
        type: '',
        status: '',
        customerId: '',
        customerName: '',
        color: '',
        notes: '',
        contractNumber: '',
        language: 'Magyar / Hungarian',
        paymentDetails: {
          nftCount: 0,
          splitPayment: '',
          nftValueUSDT: 0,
          currency: 'USDT',
          paymentType: 'KRIPTO',
          paymentMethod: 'KRIPTO (Utalás)',
          amount: 0
        }
      });
      fetchProjects();
    } catch (err) {
      console.error('Error creating project:', err);
      toast.error('Hiba történt a projekt létrehozásakor');
    }
  };

  const handleCsvImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        encoding: "ISO-8859-2",
        complete: async (results) => {
          try {
            setLoading(true);
            let successCount = 0;
            let errorCount = 0;
            
            for (const row of results.data) {
              try {
                // Teljes cím összeállítása
                const fullAddress = [
                  row['Közterület neve'],
                  row['Közterület jellege'],
                  row['Házszám'],
                  row['Épület'],
                  row['Lépcsőház'],
                  row['Emelet'],
                  row['Ajtó']
                ].filter(Boolean).join(' ');

                // Customer adatok összeállítása
                const customerData = {
                  customerId: row['#'] || '',
                  name: row['Ügyfél neve'] || '',
                  address: {
                    zipCode: row['Irányítószám'] || '',
                    city: row['Város'] || '',
                    fullAddress: fullAddress || '',
                    country: row['Ország'] || 'Magyarország',
                    countryCode: row['Országkód'] || 'HU'
                  },
                  status: row['Státusz'] || 'Új partner',
                  taxDetails: {
                    taxNumber: row['Adószám'] || '',
                    euTaxNumber: row['Közösségi adószám'] || '',
                    groupTaxNumber: row['Csoport tag adószáma'] || '',
                    thirdCountryTaxId: row['Harmadik országbeli adóazonosító'] || '',
                    registrationNumber: row['Cégjegyzékszám'] || ''
                  },
                  payment: {
                    defaultDueDate: parseInt(row['Alapértelmezett fizetési határidő']) || 0,
                    defaultPaymentMethod: row['Alapértelmezett fizetési mód'] || 'átutalás'
                  },
                  notes: {
                    invoiceNote: row['Ügyfél számla megjegyzés'] || '',
                    internalNote: row['Ügyfél belső megjegyzés'] || ''
                  },
                  primaryContact: {
                    lastName: row['Alapértelmezett kapcsolattartó vezetéknév'] || '',
                    firstName: row['Alapértelmezett kapcsolattartó keresztnév'] || '',
                    email: row['Alapértelmezett kapcsolattartó e-mail cím'] || '',
                    phone: row['Alapértelmezett kapcsolattartó telefonszám'] || '',
                    position: row['Alapértelmezett kapcsolattartó beosztása'] || '',
                    newsletter: row['Alapértelmezett kapcsolattartó hírlevélre feliratkozva'] === 'Igen'
                  },
                  secondaryContact: {
                    lastName: row['Kapcsolattartó vezetéknév'] || '',
                    firstName: row['Kapcsolattartó keresztnév'] || '',
                    email: row['Kapcsolattartó e-mail cím'] || '',
                    phone: row['Kapcsolattartó telefonszám'] || '',
                    position: row['Kapcsolattartó beosztása'] || '',
                    newsletter: row['Kapcsolattartó hírlevélre feliratkozva'] === 'Igen'
                  },
                  createdAt: Timestamp.now(),
                  updatedAt: Timestamp.now()
                };

                // Adatok mentése a customers kollekcióba
                await addDoc(collection(db, 'customers'), customerData);
                successCount++;
              } catch (error) {
                console.error('Error importing customer:', error);
                errorCount++;
                toast.error(`Hiba a(z) ${row['Ügyfél neve']} importálása során`);
              }
            }

            toast.success(`${successCount} ügyfél sikeresen importálva${errorCount > 0 ? `, ${errorCount} hibával` : ''}`);
            fetchProjects();
          } catch (error) {
            console.error('Error during import:', error);
            toast.error('Hiba történt az importálás során');
          } finally {
            setLoading(false);
          }
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          toast.error('Hiba a CSV fájl feldolgozása során');
          setLoading(false);
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Add Import Button next to New Project Button */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('projects.title')}</h1>
        <div className="flex gap-2">
          {/* CSV Import */}
          <label className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 cursor-pointer">
            <Icons.FileUp className="h-5 w-5" />
            {t('projects.import')}
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvImport}
              className="hidden"
            />
          </label>
          
          {/* Existing New Project Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Icons.Plus className="h-5 w-5" />
            {t('projects.new')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="rounded-lg border-gray-300"
        >
          <option value="">Minden státusz</option>
          <option value="Befizetést várunk - KRIPTO">Befizetést várunk - KRIPTO</option>
          <option value="Díjbkérő kiküldése - HUN">Díjbkérő kiküldése - HUN</option>
          <option value="Feldolgozva">Feldolgozva</option>
          <option value="Inaktív">Inaktív</option>
          <option value="Stake kiírás">Stake kiírás</option>
        </select>

        <select
          value={filters.type}
          onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
          className="rounded-lg border-gray-300"
        >
          <option value="">Minden típus</option>
          <option value="Tipus FÜLÓRA">Tipus FÜLÓRA</option>
          <option value="FÜLÓRA NFT - Vásárlás">FÜLÓRA NFT - Vásárlás</option>
          <option value="FÜLÓRA - Üzemanyag NFT">FÜLÓRA - Üzemanyag NFT</option>
          <option value="FÜLÓRA - Bér kifizetése NFT">FÜLÓRA - Bér kifizetése NFT</option>
        </select>

        <select
          value={filters.dateRange}
          onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
          className="rounded-lg border-gray-300"
        >
          <option value="all">Minden idő</option>
          <option value="today">Mai nap</option>
          <option value="week">Utóbbi 1 hét</option>
          <option value="month">Utóbbi 1 hónap</option>
        </select>
      </div>

      {/* New Project Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('projects.new')}</h2>
              <button onClick={() => setIsModalOpen(false)}>
                <Icons.X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateProject}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('projects.table.name')}
                  </label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('projects.table.type')}
                  </label>
                  <select
                    value={newProject.type}
                    onChange={(e) => setNewProject(prev => ({ ...prev, type: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  >
                    <option value="">{t('projects.selectType')}</option>
                    <option value="Tipus FÜLÓRA">{t('projects.types.earphone')}</option>
                    <option value="FÜLÓRA NFT - Vásárlás">{t('projects.types.earphoneNft')}</option>
                    <option value="FÜLÓRA - Üzemanyag NFT">{t('projects.types.fuelNft')}</option>
                    <option value="FÜLÓRA - Bér kifizetése NFT">{t('projects.types.salaryNft')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('projects.table.status')}
                  </label>
                  <select
                    value={newProject.status}
                    onChange={(e) => setNewProject(prev => ({ ...prev, status: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  >
                    <option value="">{t('projects.selectStatus')}</option>
                    <option value="Befizetést várunk - KRIPTO">{t('projects.status.waitingCrypto')}</option>
                    <option value="Díjbkérő kiküldése - HUN">{t('projects.status.waitingHuf')}</option>
                    <option value="Feldolgozva">{t('projects.status.processed')}</option>
                    <option value="Inaktív">{t('projects.status.inactive')}</option>
                    <option value="Stake kiírás">{t('projects.status.stakeWriting')}</option>
                  </select>
                </div>
                {/* Add more form fields as needed */}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Projekt neve
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ügyfél
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Létrehozva
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projects.map((project) => (
              <tr key={project.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{project.name}</div>
                  <div className="text-sm text-gray-500">{project.type}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{project.customerName}</div>
                  <div className="text-sm text-gray-500">{project.customerId}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {project.createdAt?.toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-indigo-600 hover:text-indigo-900 mr-4">
                    Szerkesztés
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    Törlés
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
} 